import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { playNote } from "@/lib/audio";
import { Sparkle, Trash, Lightning, Star, ChartLine, Disc, Play, ArrowSquareOut, Fire } from "@phosphor-icons/react";
import Visualizer from "@/components/Visualizer";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [presets, setPresets] = useState([]);
  const [sub, setSub] = useState(null);
  const [prompt, setPrompt] = useState("dark rage lead");
  const [busy, setBusy] = useState(false);
  const [latest, setLatest] = useState(null);
  const [credits, setCredits] = useState(null);
  const [err, setErr] = useState("");
  const [streak, setStreak] = useState(null);

  // Only redirect on first mount if not authenticated — never on logout (logout handles redirect).
  useEffect(() => {
    if (!loading && !user && window.location.pathname === "/dashboard") {
      // Defer slightly so a logout-driven nav('/') from Nav wins the race.
      const t = setTimeout(() => {
        if (!localStorage.getItem("bl_token")) navigate("/auth?mode=login");
      }, 50);
      return () => clearTimeout(t);
    }
  }, [loading, user, navigate]);

  const loadPresets = async () => {
    try {
      const { data } = await api.get("/presets");
      setPresets(data);
    } catch {}
  };
  const loadSub = async () => {
    try {
      const { data } = await api.get("/subscriptions/me");
      setSub(data && data.tier ? data : null);
    } catch {}
  };
  const loadStreak = async () => {
    try {
      const { data } = await api.get("/streaks/me");
      setStreak(data);
    } catch {}
  };

  useEffect(() => {
    if (user) { loadPresets(); loadSub(); loadStreak(); }
  }, [user]);

  const generate = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/ai/generate-preset", { prompt });
      setLatest(data.preset);
      setCredits(data.credits);
      loadStreak();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!latest) return;
    await api.post("/presets", { preset: latest });
    loadPresets();
  };

  const remove = async (id) => {
    await api.delete(`/presets/${id}`);
    loadPresets();
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-ink-dim">LOADING…</div>;

  return (
    <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-brand-red mb-2">◢ STUDIO / {user.producer_name?.toUpperCase()}</div>
          <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-6xl leading-[0.9]">
            COMMAND <span className="text-brand-red neon-red">DECK.</span>
          </h1>
        </div>
        <div className="flex gap-3">
          <Link to="/playground" className="border border-zinc-700 px-4 py-3 text-[10px] tracking-[0.3em] uppercase hover:border-brand-red hover:text-brand-red inline-flex items-center gap-2" data-testid="dashboard-open-playground">
            <Play size={12} weight="fill" /> PLAYGROUND
          </Link>
          <Link to="/pricing" className="border border-zinc-700 px-4 py-3 text-[10px] tracking-[0.3em] uppercase hover:border-brand-purple hover:text-brand-purple inline-flex items-center gap-2" data-testid="dashboard-open-pricing">
            <Star size={12} weight="fill" /> PLANS
          </Link>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-900 border border-zinc-900 mb-8">
        <StatCard label="ACTIVE PLAN" value={sub?.tier ? sub.tier.toUpperCase() : "—"} sub={sub?.status || "Not subscribed"} icon={<Lightning size={14} weight="bold" className="text-brand-red" />} testid="stat-plan" />
        <StatCard label="AI CREDITS TODAY" value={`${user.ai_credits_used_today ?? 0}`} sub={creditLimitLabel(user.tier)} icon={<Sparkle size={14} weight="bold" className="text-brand-purple" />} testid="stat-credits" />
        <StatCard label="PRESETS SAVED" value={presets.length} sub="Cloud synced" icon={<Disc size={14} weight="bold" className="text-brand-red" />} testid="stat-presets" />
        <StatCard
          label="STREAK"
          value={`${streak?.current_streak ?? 0}d`}
          sub={
            streak?.is_on_fire
              ? `🔥 NEXT: ${streak.next_milestone}d`
              : streak?.current_streak > 0
                ? `Best: ${streak.longest_streak}d`
                : "Login daily to start"
          }
          icon={<Fire size={14} weight={streak?.is_on_fire ? "fill" : "bold"} className={streak?.is_on_fire ? "text-brand-red animate-pulse" : "text-ink-mute"} />}
          testid="stat-streak"
        />
        <StatCard label="LAB SCORE" value={`+${42 + presets.length * 3}`} sub="Producer rank" icon={<ChartLine size={14} weight="bold" className="text-brand-green" />} testid="stat-score" />
      </div>

      {/* Subscription banner if not subscribed */}
      {!sub?.tier && (
        <div className="mb-8 border border-brand-red/40 bg-brand-red/5 p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-testid="dashboard-upgrade-banner">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-1">FREE ACCESS</div>
            <div className="text-sm">You&apos;re on the demo plan — 3 AI generations / day. <span className="text-ink-mute">Subscribe to unlock the full lab.</span></div>
          </div>
          <Link to="/pricing" className="bg-brand-red text-black text-xs font-bold tracking-[0.25em] uppercase px-5 py-3 hover:bg-white clip-arrow-r" data-testid="dashboard-upgrade-btn">
            UNLOCK ▸
          </Link>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-6">
        {/* AI Preset Generator */}
        <div className="lg:col-span-7 border border-zinc-800 bg-bg-surface p-6 grain">
          <div className="flex items-center gap-2 mb-4">
            <Sparkle size={16} weight="fill" className="text-brand-red" />
            <span className="text-[10px] tracking-[0.4em] uppercase text-ink-dim">AI PRESET GENERATOR</span>
          </div>
          <div className="flex border border-zinc-800 focus-within:border-brand-red">
            <span className="px-3 text-brand-red font-bold flex items-center">›</span>
            <input
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && generate()}
              data-testid="dashboard-prompt-input"
              placeholder="describe a sound…"
              className="flex-1 bg-transparent py-3 text-sm font-mono outline-none"
            />
            <button onClick={generate} disabled={busy} data-testid="dashboard-generate-btn" className="bg-brand-red text-black text-[10px] tracking-[0.3em] uppercase font-bold px-4 hover:bg-white disabled:opacity-50">
              {busy ? "..." : "GEN ▸"}
            </button>
          </div>
          {err && <div className="mt-3 text-xs text-brand-red" data-testid="dashboard-error">{err}</div>}

          <div className="mt-5">
            <Visualizer height={120} style="wave" color={latest?.color_primary || "#FF003C"} color2={latest?.color_secondary || "#8B00FF"} />
          </div>

          {latest && (
            <div className="mt-5 border border-zinc-900 p-4 bg-black/60" data-testid="dashboard-latest-preset">
              <div className="flex items-baseline justify-between">
                <div className="font-display text-2xl font-black tracking-tighter">{latest.name}</div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">{latest.waveform}</div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mt-3 text-[10px]">
                <Mini l="DRIVE" v={latest.drive} />
                <Mini l="FILTER" v={latest.filter_cutoff} />
                <Mini l="REVERB" v={latest.reverb} />
                <Mini l="DELAY" v={latest.delay} />
                <Mini l="BITCR" v={latest.bitcrush} />
                <Mini l="WIDTH" v={latest.stereo_width} />
              </div>
              <div className="mt-4 flex gap-2 flex-wrap">
                <button onClick={() => [60, 63, 67].forEach((n, i) => setTimeout(() => playNote(latest, n, 0.45), i * 200))} className="border border-zinc-700 px-3 py-2 text-[10px] tracking-[0.25em] uppercase hover:border-brand-purple hover:text-brand-purple inline-flex items-center gap-1.5" data-testid="dashboard-audition-btn">
                  <Play size={12} weight="fill" /> AUDITION
                </button>
                <button onClick={save} className="bg-brand-red text-black px-3 py-2 text-[10px] tracking-[0.25em] uppercase font-bold hover:bg-white" data-testid="dashboard-save-preset-btn">
                  SAVE PRESET ▸
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Saved Presets list */}
        <div className="lg:col-span-5 border border-zinc-800 bg-bg-surface p-6 grain">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Disc size={16} weight="fill" className="text-brand-red" />
              <span className="text-[10px] tracking-[0.4em] uppercase text-ink-dim">YOUR PRESETS · {presets.length}</span>
            </div>
            <Link to="/playground" className="text-[10px] tracking-[0.3em] uppercase text-ink-mute hover:text-brand-red inline-flex items-center gap-1">
              EDIT IN LAB <ArrowSquareOut size={12} />
            </Link>
          </div>
          {presets.length === 0 ? (
            <div className="border border-dashed border-zinc-800 p-10 text-center" data-testid="presets-empty">
              <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim mb-2">EMPTY LIBRARY</div>
              <div className="text-sm text-ink-mute">Generate your first preset to start building your sonic identity.</div>
            </div>
          ) : (
            <ul className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
              {presets.map((p, idx) => (
                <li key={`${p.id}-${idx}`} className="border border-zinc-900 bg-black/40 hover:border-zinc-700 transition-colors" data-testid={`preset-row-${p.id}`}>
                  <div className="flex items-center gap-3 p-3">
                    <div className="w-1.5 h-10" style={{ background: `linear-gradient(180deg, ${p.color_primary}, ${p.color_secondary})` }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold uppercase tracking-tight text-sm truncate">{p.name}</div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-ink-dim mt-0.5 truncate">
                        {p.waveform} · {p.tags.slice(0, 3).join(" / ")}
                      </div>
                    </div>
                    <button onClick={() => [60, 64, 67].forEach((n, i) => setTimeout(() => playNote(p, n, 0.4), i * 180))} data-testid={`preset-play-${p.id}`} className="text-ink-mute hover:text-brand-red p-2" title="Play">
                      <Play size={14} weight="fill" />
                    </button>
                    <button onClick={() => remove(p.id)} data-testid={`preset-delete-${p.id}`} className="text-ink-mute hover:text-brand-red p-2" title="Delete">
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Marketplace teaser */}
      <div className="mt-10 border border-zinc-800 bg-bg-surface p-8 relative grain overflow-hidden" data-testid="marketplace-teaser">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1610716632424-4d45990bcd48?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')", backgroundSize: "cover" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="relative grid md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-brand-red mb-2">◢ MARKETPLACE — COMING SOON</div>
            <h3 className="font-display font-black uppercase tracking-tighter text-3xl sm:text-4xl leading-[0.9]">
              SELL YOUR<br /><span className="text-brand-purple">PRESETS.</span> KEEP 80%.
            </h3>
            <p className="text-sm text-ink-mute mt-4 max-w-md">
              Pro tier unlocks marketplace selling. Drop your packs, set your price, and let other producers
              build the next sound off your blueprint.
            </p>
          </div>
          <div className="md:text-right">
            <Link to="/pricing" className="inline-block bg-brand-purple text-black text-xs font-bold tracking-[0.25em] uppercase px-6 py-3.5 hover:bg-white clip-arrow-r" data-testid="marketplace-cta">
              UPGRADE TO PRO ▸
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon, testid }) {
  return (
    <div className="bg-bg-surface p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">{label}</span>
        {icon}
      </div>
      <div className="font-display font-black text-3xl tracking-tighter">{value}</div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-ink-mute mt-1">{sub}</div>
    </div>
  );
}

function Mini({ l, v }) {
  return (
    <div className="border border-zinc-900 px-2 py-1.5 bg-black/40">
      <div className="text-[8px] tracking-[0.25em] uppercase text-ink-dim">{l}</div>
      <div className="text-white font-mono text-xs">{typeof v === "number" ? v.toFixed(2) : v}</div>
    </div>
  );
}

function creditLimitLabel(tier) {
  if (!tier) return "Free: 3 / day";
  if (tier === "standard") return "Standard: 5 / day";
  if (tier === "intermediate") return "Pro: 50 / day";
  if (tier === "pro") return "Unlimited";
  return "—";
}
